/**
 * FeedbackCard/styles.tsx — 8211-A
 *
 * Styled components for the FeedbackCard.
 * Uses theme variables from themeVariables.js for theme consistency.
 * No hardcoded pixel heights — all dynamic sizing via flex.
 */
import styled, { css, keyframes } from "styled-components/macro";
import { themeVariables } from "../../themeVariables";

/** Subtle wiggle animation for when a comment is opened from chat navigation */
const wiggle = keyframes`
  0% { transform: rotate(0deg); }
  15% { transform: rotate(-0.8deg); }
  30% { transform: rotate(0.6deg); }
  45% { transform: rotate(-0.4deg); }
  60% { transform: rotate(0.2deg); }
  75% { transform: rotate(-0.1deg); }
  100% { transform: rotate(0deg); }
`;

/** Root card container — collapsed or expanded */
export const CardContainer = styled.div<{ $isExpanded: boolean; $shouldWiggle?: boolean }>`
  align-self: stretch;
  border-radius: 16px;
  background-color: ${themeVariables.menuColorFullscreen};
  color: ${themeVariables.grey0};
  border: 1px solid var(--border-basic-hover, ${themeVariables.grey2});
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  padding: 16px;
  gap: 12px;
  cursor: pointer;
  transition:
    box-shadow 0.2s ease,
    border-color 0.2s ease;

  ${({ $isExpanded }) =>
    $isExpanded &&
    `
    box-shadow: 0 0 15px rgba(0, 0, 0, 0.15), 0 0 2px rgba(0, 0, 0, 0.15);
    border-color: var(--border-basic-hover, ${themeVariables.grey2});
  `}

  &:hover {
    box-shadow:
      0 0 15px rgba(0, 0, 0, 0.15),
      0 0 2px rgba(0, 0, 0, 0.15);
    border-color: var(--border-basic-hover, ${themeVariables.grey2});
  }

  ${({ $shouldWiggle }) =>
    $shouldWiggle &&
    css`
      animation: ${wiggle} 0.9s ease-in-out;
    `}
`;

/** Header row: label on left, action icons on right */
export const CardHeaderRow = styled.div`
  align-self: stretch;
  display: flex;
  align-items: center;
`;

/** Category label group (icon + text) */
export const CategoryLabelGroup = styled.div`
  flex: 1;
  min-width: 0;
  display: flex;
  align-items: center;
  gap: 0.5rem;
  color: ${themeVariables.grey1};
`;

/** The category label text (e.g. "FEEDBACK . DEVELOPMENTAL") */
export const CategoryLabel = styled.span`
  font-size: 0.875rem;
  font-weight: 600;
  letter-spacing: 0.1em;
  line-height: 120%;
  color: ${themeVariables.grey1};
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

/** Wrapper for hover action icons (resolve, three-dot menu) */
export const HoverActions = styled.div`
  display: flex;
  align-items: center;
  gap: 1rem;
  opacity: 0;
  transition: opacity 0.15s ease;

  ${CardContainer}:hover & {
    opacity: 1;
  }
`;

/** Card text content area */
export const CardTextContent = styled.div<{ $truncate: boolean }>`
  align-self: stretch;
  font-size: 16px;
  line-height: 1.45;
  color: ${themeVariables.grey0};

  body[data-theme="cauldron"] & {
    color: var(--grey-1);
  }

  ${({ $truncate }) =>
    $truncate &&
    `
    display: -webkit-box;
    -webkit-box-orient: vertical;
    -webkit-line-clamp: 2;
    line-clamp: 2;
    /* Keep collapsed card height steady while streaming text updates in. */
    height: calc(1.5em * 2);
    overflow: hidden;
  `}
`;

export const CollapsedReplyIndicator = styled.div`
  align-self: stretch;
  text-align: center;
  font-size: 12px;
  line-height: 1.2;
  color: ${themeVariables.grey2};
`;

/** Light metadata label for edited comments/replies. */
export const EditedLabel = styled.div`
  align-self: stretch;
  font-size: 0.75rem;
  line-height: 1.2;
  color: ${themeVariables.grey2};
`;

/** Diff container with border */
export const DiffContainer = styled.div`
  align-self: stretch;
  border-radius: 0.5rem;
  border: 1px solid ${themeVariables.grey3};
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 1rem;
  gap: 0.625rem;
  font-size: 1rem;
  line-height: 1.3125rem;
  color: ${themeVariables.grey0};

  /* Override DiffMatchPatchViewer inline styles for added text */
  span[style*="color: green"] {
    color: ${themeVariables.primaryCTAHighContrast} !important;
    background-color: ${themeVariables.primaryCTATransparent10} !important;
  }

  /* Override deleted text for better contrast on dark themes */
  span[style*="color: gray"] {
    color: ${themeVariables.grey1} !important;
  }
`;

/** Controls row at bottom of diff box */
export const DiffControlsRow = styled.div`
  align-self: stretch;
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-top: 0.625rem;

  svg {
    path {
      stroke: ${themeVariables.grey0};
      stroke-width: 0.5;
    }
  }
`;

/** Left-side controls in diff row (apply + copy) */
export const DiffLeftActions = styled.div`
  display: flex;
  align-items: center;
  gap: 0.75rem;
`;

/** Apply action shown on left side of controls row */
export const DiffApplyButton = styled.div`
  width: 2rem;
  height: 2rem;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.25rem;
  cursor: pointer;
  font-size: 1rem;
  font-weight: 500;
  color: ${themeVariables.grey0};
  user-select: none;

  &:hover {
    opacity: 0.7;
  }

  svg {
    width: 1.375rem;
    height: 1.375rem;

    path,
    circle {
      fill: currentColor;
      stroke: none;
    }
  }
`;

/** Copy action shown immediately right of Apply */
export const DiffCopyButton = styled.div`
  width: 2rem;
  height: 2rem;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  color: ${themeVariables.grey0};
  user-select: none;

  &:hover {
    opacity: 0.7;
  }

  svg {
    width: 1.375rem;
    height: 1.375rem;

    path,
    rect,
    circle {
      fill: currentColor;
      stroke: none;
    }
  }
`;

/** Thumbs up/down row — right-aligned inside DiffContainer */
export const ThumbsRow = styled.div`
  display: flex;
  align-items: center;
  gap: 0.75rem;
`;

/** Individual thumb button */
export const ThumbButton = styled.div`
  width: 2rem;
  height: 2rem;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  user-select: none;

  &:hover {
    opacity: 0.7;
  }

  svg {
    width: 1.375rem;
    height: 1.375rem;

    path {
      fill: ${themeVariables.grey1};
    }
  }
`;
